import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DsAlert,
  DsBadge,
  DsButton,
  DsEmptyState,
  DsInput,
  DsPageHeader,
  DsSpinner,
  PAGE_LAYOUT,
} from "../../../design-system";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { activityLogService } from "../../../services/api";

function formatWhen(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

export default function ActivityLogMonitor() {
  const navigate = useNavigate();
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canView =
    isAdmin || hasScreenAccess("workspaces", "activity_log_monitor", "view");
  const canExport =
    isAdmin || hasScreenAccess("workspaces", "activity_log_monitor", "export");

  const [meta, setMeta] = useState(null);
  const [mode, setMode] = useState("hot"); // hot | archive
  const [moduleKey, setModuleKey] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [archives, setArchives] = useState([]);
  const [archiveId, setArchiveId] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const modules = useMemo(
    () => ["all", ...(meta?.modules || [])],
    [meta]
  );

  const loadMeta = useCallback(async () => {
    try {
      const res = await activityLogService.getMeta();
      setMeta(res?.data || res);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError("");
    try {
      if (mode === "archive") {
        if (!archiveId) {
          const res = await activityLogService.listArchives({ limit: 50 });
          const data = res?.data || res;
          setArchives(data?.rows || []);
          setRows([]);
          setTotal(data?.total || 0);
        } else {
          const res = await activityLogService.getArchiveRows(archiveId, {
            moduleKey,
            status,
            q,
            limit: 100,
          });
          const data = res?.data || res;
          setRows(data?.rows || []);
          setTotal(data?.total || 0);
        }
      } else {
        const res = await activityLogService.list({
          moduleKey,
          status,
          q,
          limit: 100,
        });
        const data = res?.data || res;
        setRows(data?.rows || []);
        setTotal(data?.total || 0);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView, mode, archiveId, moduleKey, status, q]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const onExport = async () => {
    if (!canExport) return;
    setExporting(true);
    try {
      await activityLogService.exportExcel({ moduleKey, status, q });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!canView) {
    return (
      <div className={PAGE_LAYOUT.page}>
        <div className={PAGE_LAYOUT.shell}>
          <div className={PAGE_LAYOUT.content}>
            <DsEmptyState
              title="Access denied"
              description="You do not have permission to view Activity Log Monitor. Ask an admin to enable it in User Role Management → Screen Access."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={PAGE_LAYOUT.page}>
      <div className={PAGE_LAYOUT.shell}>
        {error ? (
          <div className={PAGE_LAYOUT.alert}>
            <DsAlert tone="danger">{error}</DsAlert>
          </div>
        ) : null}
        <div className={PAGE_LAYOUT.body}>
          <aside className={PAGE_LAYOUT.aside}>
            <div className={PAGE_LAYOUT.asideHead}>
              <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Settings</p>
              <h2 className="ds-h4">Activity Log</h2>
            </div>
            <div className={PAGE_LAYOUT.asideNav}>
              <button
                type="button"
                className={
                  mode === "hot"
                    ? PAGE_LAYOUT.navItemActive
                    : PAGE_LAYOUT.navItem
                }
                onClick={() => {
                  setMode("hot");
                  setArchiveId("");
                }}
              >
                Hot log (30 days)
              </button>
              <button
                type="button"
                className={
                  mode === "archive"
                    ? PAGE_LAYOUT.navItemActive
                    : PAGE_LAYOUT.navItem
                }
                onClick={() => {
                  setMode("archive");
                  setArchiveId("");
                }}
              >
                Archives (1 year)
              </button>
              <button
                type="button"
                className={PAGE_LAYOUT.navItem}
                onClick={() => navigate("/admin/workspace/settings")}
              >
                Back to Settings
              </button>
            </div>
            <div className={PAGE_LAYOUT.asideFoot}>
              <p className="ds-small text-[hsl(var(--ds-muted-fg))]">
                Hot {meta?.hotDays || 30}d · Archive{" "}
                {meta?.archiveKeepDays || 365}d · PII masked · This company only
              </p>
            </div>
          </aside>

          <div className={PAGE_LAYOUT.main}>
            <div className={PAGE_LAYOUT.contentFlush}>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3 px-1">
                <DsPageHeader
                  title="Activity Log Monitor"
                  description={
                    mode === "hot"
                      ? "Live audit of product activity for this company."
                      : "Compressed archives — select a period to browse."
                  }
                />
                <div className="flex flex-wrap gap-2">
                  {canExport && mode === "hot" ? (
                    <DsButton
                      variant="secondary"
                      onClick={onExport}
                      disabled={exporting}
                    >
                      {exporting ? "Exporting…" : "Export Excel"}
                    </DsButton>
                  ) : null}
                  <DsButton
                    variant="ghost"
                    onClick={() => navigate("/admin/workspace/settings")}
                  >
                    Close
                  </DsButton>
                </div>
              </div>

              <div className={PAGE_LAYOUT.toolbar}>
                <select
                  className="h-9 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm"
                  value={moduleKey}
                  onChange={(e) => setModuleKey(e.target.value)}
                >
                  {modules.map((m) => (
                    <option key={m} value={m}>
                      {m === "all" ? "All modules" : m}
                    </option>
                  ))}
                </select>
                <select
                  className="h-9 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="all">All statuses</option>
                  <option value="Success">Success</option>
                  <option value="Failed">Failed</option>
                </select>
                <DsInput
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search employee / user / text…"
                  className="min-w-[220px]"
                />
                {mode === "archive" ? (
                  <select
                    className="h-9 min-w-[200px] rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm"
                    value={archiveId}
                    onChange={(e) => setArchiveId(e.target.value)}
                  >
                    <option value="">Select archive period…</option>
                    {archives.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.periodFrom} → {a.periodTo} ({a.rowCount} rows)
                      </option>
                    ))}
                  </select>
                ) : null}
                <DsBadge tone="neutral">{total} rows</DsBadge>
              </div>

              <div className={`${PAGE_LAYOUT.panel} mt-3 overflow-hidden`}>
                {loading ? (
                  <div className="flex justify-center py-16">
                    <DsSpinner />
                  </div>
                ) : mode === "archive" && !archiveId ? (
                  archives.length === 0 ? (
                    <DsEmptyState
                      title="No archives yet"
                      description="Rows older than 30 days are compressed nightly into archives."
                    />
                  ) : (
                    <div className="p-4">
                      <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
                        Select an archive period above to browse compressed
                        history.
                      </p>
                    </div>
                  )
                ) : rows.length === 0 ? (
                  <DsEmptyState
                    title="No activity yet"
                    description="Mutating API actions and email deliveries will appear here."
                  />
                ) : (
                  <div className="overflow-auto">
                    <table className="ds-table w-full text-left text-sm">
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>Module</th>
                          <th>Employee</th>
                          <th>Activity</th>
                          <th>User</th>
                          <th>Status</th>
                          <th>Attempt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id || `${r.occurredAt}-${r.activity}`}>
                            <td className="whitespace-nowrap">
                              {formatWhen(r.occurredAt)}
                            </td>
                            <td>{r.moduleKey}</td>
                            <td>{r.employeeLabel || "—"}</td>
                            <td className="max-w-md">{r.activity}</td>
                            <td>{r.actorLabel || "—"}</td>
                            <td>
                              <DsBadge
                                tone={
                                  r.status === "Failed" ? "danger" : "success"
                                }
                              >
                                {r.status}
                              </DsBadge>
                            </td>
                            <td>{r.attempt || 1}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
